import { useEffect, useMemo, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'

import { COLLECTIONS } from '__constants__'
import PropTypes from 'prop-types'
import UserContext from './UserContext'
import auth from 'services/firebase/auth'
import firestore from 'services/firebase/firestore'
import getLastSessionFromLocalStorage from 'domains/Session/hooks/useSessionActions/actions/getLastSessionFromLocalStorage'
import saveUserToDB from 'domains/Session/hooks/useSessionActions/actions/saveUserToDB'
import setSessionToLocalStorage from 'domains/Session/hooks/useSessionActions/actions/setSessionToLocalStorage'
import updateEmailVerificationStatus from 'domains/Session/hooks/useSessionActions/actions/updateEmailVerificationStatus'
import updateGDPRStatus from 'domains/Session/hooks/useSessionActions/actions/updateGDPRStatus'
import { useAuthState } from 'react-firebase-hooks/auth'
import useGDPRStatus from 'domains/Session/hooks/useGDPRStatus'
import useHandleError from 'hooks/useHandleError'

const UserProvider = ({ children }) => {
  /* The above code is a function that takes in a callback function as an argument.
  The callback function is then called with the error as an argument. */
  const handleError = useHandleError()

  /* Using the useGDPRStatus hook to get the GDPR status of the user. */
  const gdpr = useGDPRStatus()

  /* Using the useAuthState hook to get the user from the firebase auth state. */
  const [authUser, authLoading] = useAuthState(auth)

  /* If the user is logged in, fetch the user's data from Firestore. */
  const [value, setValue] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Initial user saving to the DB
  useEffect(() => {
    let unsubscribe
    if (authUser && !authLoading) {
      unsubscribe = onSnapshot(
        doc(firestore, COLLECTIONS.USERS, authUser.uid),
        {
          next: (snapshot) => {
            if (!snapshot?.exists) {
              return saveUserToDB({
                _id: authUser.uid,
                email: authUser.email,
                avatarUrl: authUser.photoURL,
                agreement: true,
                gdpr,
                onError: handleError
              })
            }
            const data = snapshot.data()
            setValue(data)
            setLoading(false)
          },
          error: setError
        }
      )
    }
    if (!authUser && !authLoading) {
      setValue(null)
      setLoading(false)
    }
    return () => {
      unsubscribe?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, authLoading])

  // Updating user's email verification status
  useEffect(() => {
    // Check if data is loaded
    const isUserDataLoaded = authUser && value && !loading

    // Updating email verification status
    isUserDataLoaded &&
      !value.emailVerified &&
      updateEmailVerificationStatus({
        _id: authUser.uid,
        sessionUserEmailVerified: authUser?.emailVerified,
        dbUserEmailVerified: value?.emailVerified,
        onError: handleError
      })
  }, [authUser, value, loading, handleError])

  // Updating LS with user session info
  useEffect(() => {
    const isUserDataLoaded = authUser && value && !loading
    if (isUserDataLoaded) {
      const lastSession = getLastSessionFromLocalStorage()
      lastSession?.email !== authUser?.email &&
        /* Set the session to the user's local storage. */
        setSessionToLocalStorage({
          email: authUser.email,
          avatarUrl: authUser.photoURL,
          provider: localStorage.getItem('lastSessionProvider')
        })
    }
  }, [authUser, value, loading])

  // Update GDPR status
  useEffect(() => {
    /* This is a ternary operator. It is checking if the user, value, and loading variables are all
    true. If they are, it will return true. Otherwise, it will return false. */
    const isUserDataLoaded = authUser && value && !loading

    /* If the user is logged in, and the user's data is loaded, and the user's data has not yet been
    updated to reflect the new GDPR status, then update the user's data to reflect the new GDPR
    status. */
    if (isUserDataLoaded && !value?.gdpr && gdpr !== value?.gdpr)
      updateGDPRStatus({ _id: authUser?.uid, gdpr, onError: handleError })
  }, [gdpr, authUser, value, handleError, loading])

  // Handling user fetching error
  useEffect(() => {
    error && handleError(error)
  }, [error, handleError])

  // Preparing context
  const contextData = useMemo(() => {
    const isUserLoaded = value && !loading
    return { user: value, loading, loaded: isUserLoaded }
  }, [loading, value])

  return (
    <UserContext.Provider value={contextData}>{children}</UserContext.Provider>
  )
}

UserProvider.propTypes = { children: PropTypes.node }

export default UserProvider
